import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  FormGroup,
  Input,
  Label,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Row,
  Spinner,
} from "reactstrap";
import restro from "../../../../../assets/orderingSystem/restrologo.png";
import ItemService from "../../../../../services/OrderingServices/ItemService";

const SmallScreenProductDetail = ({
  isOpen,
  toggle,
  selectedItem,
  count,
  setCount,
  setSelectedAttributes,
  selectedAttributes,
  handleAddToCart,
  setNotes,
  notes,
  setComplementary,
  complementary,
  loading,
  setSelectedChildAttributes,
  selectedChildAttributes,
}) => {
  // console.log("🚀 ~ ProductDetailModal ~ selectedItem:", selectedItem);
  const dispatch = useDispatch();
  const cart = useSelector((state) => state.cart.items);
  const [currentStep, setCurrentStep] = useState(1);

  // --- spicy levels ---
  const spicyLevel = {
    1: "Not Spicy",
    2: "Mild",
    3: "Medium",
    4: "Spicy",
    5: "Very Spicy",
    6: "Extra Spicy",
  };

  // --- handle attribute selection (parent attributes) ---
  // const handleSelectAttribute = (itemId, attrName, attributeData) => {
  //   setSelectedAttributes((prev) => ({
  //     ...prev,
  //     [itemId]: {
  //       ...(prev[itemId] || {}),
  //       [attrName]: {
  //         ...attributeData,
  //         spicyLevel: prev[itemId]?.[attrName]?.spicyLevel || 0, // Preserve existing spicyLevel
  //       },
  //     },
  //   }));
  // };

  const handleSelectAttribute = (itemId, attrName, attributeData) => {
    setSelectedAttributes((prev) => {
      const currentAttr = prev[itemId]?.[attrName];

      // If the same attribute is already selected, unselect it
      if (currentAttr?.title === attributeData.title) {
        const updated = { ...prev };

        // Remove the specific attribute
        delete updated[itemId]?.[attrName];

        // If no attributes remain for this item, remove the item key entirely
        if (Object.keys(updated[itemId] || {}).length === 0) {
          delete updated[itemId];
        }

        return { ...updated };
      }

      // Otherwise, select it
      return {
        ...prev,
        [itemId]: {
          ...(prev[itemId] || {}),
          [attrName]: {
            ...attributeData,
            spicyLevel: prev[itemId]?.[attrName]?.spicyLevel || 0,
          },
        },
      };
    });
  };

  // --- update spicy level for parent attributes ---
  const handleParentSpicyChange = (itemId, spicyLevel, event) => {
    // Prevent event bubbling to parent div
    event?.stopPropagation();

    setSelectedAttributes((prev) => {
      const option = prev[itemId]?.Option || {};
      return {
        ...prev,
        [itemId]: {
          ...prev[itemId],
          Option: { ...option, spicyLevel: parseInt(spicyLevel) },
        },
      };
    });
  };

  // --- handle attribute selection (child attributes) ---
  const handleSelectChildAttribute = (
    itemId,
    productName,
    productPrice,
    item,
    attribute,
    index
  ) => {
    setSelectedChildAttributes((prev) => {
      // Find existing selections for this item
      const existingItem = prev.find((entry) => entry.productId === itemId);
      const currentSelections = existingItem ? existingItem.attributes : [];
      const maxChoices = item.choices || 1; // Default to 1 if choices not specified

      // Check if the attribute is already selected
      const isAlreadySelected = currentSelections.some(
        (attr) => attr.title === attribute.title
      );

      let updatedAttributes;
      if (isAlreadySelected) {
        // Deselect the attribute if clicked again (optional behavior)
        updatedAttributes = currentSelections.filter(
          (attr) => attr.title !== attribute.title
        );
      } else {
        // Add new attribute, respecting the choices limit
        if (currentSelections.length >= maxChoices) {
          // Replace the oldest selection if at limit
          updatedAttributes = [
            ...currentSelections.slice(1), // Keep all but the oldest
            { title: attribute.title, value: attribute.value, spicyLevel: 0 },
          ];
        } else {
          // Add the new attribute
          updatedAttributes = [
            ...currentSelections,
            { title: attribute.title, value: attribute.value, spicyLevel: 0 },
          ];
        }
      }

      // Update the selectedChildAttributes array
      const updatedSelections = prev.filter(
        (entry) => entry.productId !== itemId
      );
      if (updatedAttributes.length > 0) {
        updatedSelections.push({
          productId: itemId,
          productName,
          productPrice,
          attributes: updatedAttributes,
        });
      }

      // Note: Removed auto-advance logic - will be handled in spicy level change
      return updatedSelections;
    });
  };

  // --- update spicy level for child attributes ---
  const handleChildSpicyChange = (
    itemId,
    attributeTitle,
    spicyLevel,
    event,
    itemIndex,
    item
  ) => {
    event?.stopPropagation();

    setSelectedChildAttributes((prev) => {
      const updatedSelections = prev.map((entry) =>
        entry.productId === itemId
          ? {
              ...entry,
              attributes: entry.attributes.map((attr) =>
                attr.title === attributeTitle
                  ? { ...attr, spicyLevel: parseInt(spicyLevel) || 1 } // default fallback to 1
                  : attr
              ),
            }
          : entry
      );

      // ✅ Always consider spicyLevel >= 1 (since "Not Spicy" = 1 is default)
      const currentItem = updatedSelections.find(
        (entry) => entry.productId === itemId
      );

      const maxChoices = item?.choices || 1;
      const hasCompletedSelections =
        currentItem?.attributes?.length === maxChoices &&
        currentItem.attributes.every((attr) => attr.spicyLevel >= 1);

      if (hasCompletedSelections) {
        // Move to next child item
        const nextStep = itemIndex + 2;
        setCurrentStep((prevStep) => Math.max(prevStep, nextStep));

        if (itemIndex < data?.children?.length - 1) {
          toggleAccordion(`${nextStep}`);
        } else {
          toggleAccordion(null);
        }
      }

      return updatedSelections;
    });
  };

  const handleToggleAttribute = (attribute) => {
    setSelectedAttributes((prev) => {
      const exists = prev.find((attr) => attr.title === attribute.title);
      if (exists) {
        // Remove from selection
        return prev.filter((attr) => attr.title !== attribute.title);
      } else {
        // Add to selection
        return [...prev, attribute];
      }
    });
  };

  const handleComplementary = () => {
    setComplementary((prev) => !prev);
  };

  const [data, setData] = useState();
  const fetchDetail = async () => {
    try {
      const response = await ItemService.getDetail(selectedItem?.id);
      setData(response);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    fetchDetail();
  }, [selectedItem?.id]);

  const [openAccordion, setOpenAccordion] = useState("1");
  const toggleAccordion = (id) => {
    if (openAccordion === id) {
      setOpenAccordion();
    } else {
      setOpenAccordion(id);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setComplementary(null);
      setSelectedChildAttributes([]);
      setSelectedAttributes([]);
    }
  }, [isOpen]);

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="bottom"
      scrollable={false}
      className="d-block d-lg-none bg-light"
      style={{
        height: "90vh",
        maxHeight: "100vh",
        transition: "transform 0.3s ease",
        borderTopLeftRadius: "1rem",
        borderTopRightRadius: "1rem",
        zIndex: 1050,
      }}
    >
      <div className="d-flex flex-column h-100 ">
        <OffcanvasHeader toggle={toggle} className="">
          Customize Item
        </OffcanvasHeader>

        {/* Scrollable body */}
        <OffcanvasBody className="overflow-auto px-2">
          {/* Image & Basic Info */}
          <div>
            <Row className="mb-3">
              <Col xs={4}>
                <img
                  src={data?.image || restro}
                  className="rounded-4 w-100"
                  style={{ objectFit: "cover", height: "80px" }}
                  alt={data?.title}
                />
              </Col>
              <Col xs={8}>
                <h4 className="fw-bold mb-1">{data?.name}</h4>
                <p className="mb-1 text-muted small">{data?.description}</p>
                <h5 className="text-success fw-bold">Rs. {data?.price}</h5>
              </Col>
            </Row>
          </div>
          {/* Complementary Toggle */}
          <div className="mb-3 d-flex align-items-center d-flex justify-content-between">
            <Label check htmlFor="isComplementary" className="  fw-medium">
              Complementary
            </Label>
            <FormGroup switch className="mb-0">
              <Input
                type="switch"
                id="isComplementary"
                name="isComplementary"
                checked={complementary}
                onChange={handleComplementary}
              />
            </FormGroup>
          </div>

          {/* Quantity Selector */}
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h6 className="fw-bold">
              {complementary ? "Complementary Qty" : "Qty"}
            </h6>{" "}
            <div className="input-step rounded-5 d-flex align-items-center">
              <button
                type="button"
                className="btn btn-light btn-sm rounded-circle me-2"
                onClick={() => setCount((prev) => Math.max(1, prev - 1))}
                disabled={count <= 1}
              >
                –
              </button>
              <input
                type="number"
                className="form-control text-center"
                style={{ width: "60px" }}
                value={count}
                onChange={(e) =>
                  setCount(
                    Math.max(1, Math.min(100, parseInt(e.target.value) || 1))
                  )
                }
                min="1"
                max="100"
              />
              <button
                type="button"
                className="btn btn-light btn-sm rounded-circle ms-2"
                onClick={() => setCount((prev) => Math.min(100, prev + 1))}
              >
                +
              </button>
            </div>
          </div>

          {/* Attributes (Parent) */}
          {data?.attributes?.length > 0 && (
            <div className="mb-4">
              <Label check htmlFor="Attributes" className="fw-semibold mb-2">
                Attributes
              </Label>
              <div className="d-flex flex-column gap-2">
                {data?.attributes.map((attribute, attrIndex) => {
                  const isSelected =
                    selectedAttributes[data.id]?.["Option"]?.title ===
                    attribute.title;
                  const currentSpicyLevel =
                    selectedAttributes[data.id]?.["Option"]?.spicyLevel || 0;

                  return (
                    <div
                      key={attrIndex}
                      className={`attribute-pill px-3 py-2 rounded-3 w-100 shadow-sm ${
                        isSelected
                          ? "bg-success bg-opacity-50"
                          : "bg-white text-dark"
                      }`}
                      style={{
                        transition: "all 0.2s ease-in-out",
                      }}
                    >
                      <div
                        onClick={() =>
                          handleSelectAttribute(data.id, "Option", attribute)
                        }
                        className="d-flex justify-content-between w-100 small fw-semibold"
                        style={{ cursor: "pointer" }}
                      >
                        <span>{attribute.title}</span>
                        <span className="text-dark">
                          + Rs. {attribute.value.toFixed(2)}
                        </span>
                      </div>
                      {isSelected && (
                        <div className="mt-2">
                          <select
                            className="form-select form-select-sm mt-1"
                            value={currentSpicyLevel || 1} // default to "Not Spicy"
                            onChange={(e) =>
                              handleParentSpicyChange(
                                data.id,
                                e.target.value,
                                e
                              )
                            }
                            onClick={(e) => e.stopPropagation()}
                          >
                            {Object.entries(spicyLevel).map(
                              ([level, label]) => (
                                <option key={level} value={level}>
                                  {label}
                                </option>
                              )
                            )}
                          </select>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Product Attributes (Children) */}
          {data?.children?.map((item, index) => {
            const step = index + 1;
            const isDisabled = step > currentStep;
            const itemId = item.id || index;
            const selectedItem = Array.isArray(selectedChildAttributes)
              ? selectedChildAttributes.find(
                  (entry) => entry.productId === itemId
                )
              : null;
            const selectedAttrs = selectedItem?.attributes || [];
            const displayPrice = selectedAttrs.reduce(
              (total, attr) => total + (attr.value || 0),
              item?.price || 0
            );

            return (
              <Accordion
                open={isDisabled ? "" : openAccordion}
                toggle={
                  isDisabled ? () => {} : () => toggleAccordion(`${step}`)
                }
                key={index}
                className="mb-3 border rounded-3 shadow-sm"
              >
                <AccordionItem>
                  <AccordionHeader targetId={`${step}`} className="fw-bold">
                    <div className="d-flex justify-content-between w-100 align-items-center">
                      <span>
                        {item?.name || "Unnamed Product"}
                        {selectedAttrs.length > 0 && (
                          <span className="text-muted ms-2">
                            (
                            {selectedAttrs
                              .map(
                                (attr) =>
                                  `${attr.title}${
                                    attr.spicyLevel > 0
                                      ? ` - ${spicyLevel[attr.spicyLevel]}`
                                      : ""
                                  }`
                              )
                              .join(", ")}
                            )
                          </span>
                        )}
                      </span>
                      {displayPrice > 0 && (
                        <span className="text-muted small">
                          Rs. {displayPrice.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </AccordionHeader>
                  <AccordionBody accordionId={`${step}`} className="bg-light">
                    <div className="row">
                      {item?.attributes?.map((attribute, attrIndex) => {
                        const isSelected = selectedAttrs?.some(
                          (attr) => attr.title === attribute.title
                        );
                        const selectedAttr = selectedAttrs?.find(
                          (attr) => attr.title === attribute.title
                        );
                        const currentSpicyLevel = selectedAttr?.spicyLevel || 0;

                        return (
                          <div key={attrIndex} className="col-12">
                            <div
                              className={`attribute-pill px-3 py-2 rounded-3 shadow-sm mb-2 ${
                                isSelected
                                  ? "bg-success bg-opacity-50 text-dark"
                                  : "bg-white text-dark"
                              }`}
                              style={{
                                transition: "all 0.2s ease-in-out",
                              }}
                            >
                              <div
                                onClick={() =>
                                  handleSelectChildAttribute(
                                    itemId,
                                    item?.name,
                                    item?.price,
                                    item,
                                    attribute,
                                    index
                                  )
                                }
                                className="d-flex justify-content-between w-100 small fw-semibold"
                                style={{ cursor: "pointer" }}
                              >
                                <span>{attribute.title}</span>
                                {attribute.value > 0 && (
                                  <span className="text-dark">
                                    + Rs. {attribute.value.toFixed(2)}
                                  </span>
                                )}
                              </div>
                              {isSelected && (
                                <div className="mt-2">
                                  <select
                                    className="form-select form-select-sm mt-1"
                                    value={currentSpicyLevel}
                                    onChange={(e) =>
                                      handleChildSpicyChange(
                                        itemId,
                                        attribute.title,
                                        e.target.value,
                                        e,
                                        index,
                                        item
                                      )
                                    }
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value={0}>Select Spicy</option>
                                    {Object.entries(spicyLevel).map(
                                      ([level, label]) => (
                                        <option key={level} value={level}>
                                          {label}
                                        </option>
                                      )
                                    )}
                                  </select>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </AccordionBody>
                </AccordionItem>
              </Accordion>
            );
          })}

          {/* Notes */}
          <div className="mb-3 mt-4">
            <Label check htmlFor="Note" className="fw-semibold mb-2">
              Note
            </Label>
            <textarea
              className="form-control"
              rows="2"
              placeholder="Add some notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            ></textarea>
          </div>
        </OffcanvasBody>

        {/* Sticky Bottom Footer */}
        <div
          className="p-3 border-top bg-white shadow-lg d-flex gap-2"
          style={{ position: "sticky", bottom: 0, zIndex: 1060 }}
        >
          <button
            className="btn btn-outline-danger w-50 rounded-3"
            onClick={toggle}
          >
            Cancel
          </button>
          <button
            className="btn btn-success w-50 rounded-3"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? (
              <Spinner size="sm" />
            ) : complementary ? (
              "Complementary"
            ) : (
              "Add to Order"
            )}
          </button>
        </div>
      </div>
    </Offcanvas>
  );
};

export default SmallScreenProductDetail;
